/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react'
import { Form, Button } from 'react-bootstrap'
import LabSharedTable from '@components/pages/Lab/LabSharedTable'
import ActionButtons from '@components/common/ActionButtons/ActionButtons.component'
import PrescriptionNote from './PrescriptionNote'
import ApptToothModal from '../Appointment/ApptTooth.modal'

type PrescriptionDetail = {
  procedureType: string
  region: string
  material: string
  shadeNo: string
  instructions: string
}

type PrescriptionDetailsProps = {
  prescriptionDetails: PrescriptionDetail[]
  editingRowIndex: number | null
  onAddItem: () => void
  onEdit: (index: number) => void
  onDelete: (index: number) => void
  onSave: () => void
  onInputChange: (
    index: number,
    field: keyof PrescriptionDetail,
    value: string,
  ) => void
  onToggleNoteModal: (show: boolean) => void
}

const PrescriptionDetails: React.FC<PrescriptionDetailsProps> = ({
  prescriptionDetails,
  editingRowIndex,
  onAddItem,
  onEdit,
  onDelete,
  onSave,
  onInputChange,
  onToggleNoteModal,
}) => {
  const [showNote, setShowNote] = useState<boolean>(false)
  const [currentNote, setCurrentNote] = useState<string>('')
  const [noteRowIndex, setNoteRowIndex] = useState<number | null>(null)
  const [showRegionModal, setShowRegionModal] = useState<boolean>(false)
  const [regionRowIndex, setRegionRowIndex] = useState<number | null>(null)

  const openNoteModal = (row: any, index: number): void => {
    setCurrentNote(row.instructions)
    setNoteRowIndex(index)
    setShowNote(true)
    onToggleNoteModal(true)
  }

  const saveNote = (): void => {
    if (noteRowIndex !== null) {
      onInputChange(noteRowIndex, 'instructions', currentNote)
    }
    setShowNote(false)
    onToggleNoteModal(false)
  }

  const openRegionModal = (index: number): void => {
    setRegionRowIndex(index)
    setShowRegionModal(true)
  }

  const handleSaveRegion = (toothData: any[]): void => {
    if (regionRowIndex !== null) {
      const regionStr = toothData
        .map((item) =>
          item.surf.length
            ? item.surf.map((s: string) => `${item.tooth_num}/${s}`).join(', ')
            : `${item.tooth_num}`,
        )
        .join(', ')
      onInputChange(regionRowIndex, 'region', regionStr)
    }
    setRegionRowIndex(null)
    setShowRegionModal(false)
  }

  const procedureTypeOptions = ['Crowns', 'Dentures', 'Bridges', 'Implants']
  const materialOptions = ['Zirconia', 'Acrylic', 'Porcelain', 'Metal']
  const shadeNoOptions = ['B1', 'A1', 'C2', 'N/A', '-']

  const renderOptions = (options: string[]): JSX.Element[] =>
    options.map((option) => (
      <option key={option} value={option}>
        {option}
      </option>
    ))

  const prescriptionColumns = [
    {
      key: 'procedureType',
      label: 'Procedure type',
      formatter: (_: any, row: any) =>
        editingRowIndex !== null &&
        prescriptionDetails[editingRowIndex] === row ? (
          <Form.Select
            value={row.procedureType}
            onChange={(e) =>
              onInputChange(editingRowIndex, 'procedureType', e.target.value)
            }
            size="sm"
          >
            <option value="">Select</option>
            {renderOptions(procedureTypeOptions)}
          </Form.Select>
        ) : (
          <span>{row.procedureType}</span>
        ),
    },
    {
      key: 'region',
      label: 'Region',
      formatter: (_: any, row: any) => {
        const index = prescriptionDetails.indexOf(row)
        return (
          <Button
            variant="link"
            size="sm"
            className="p-0 text-decoration-underline"
            onClick={() => openRegionModal(index)}
          >
            {row.region ? row.region : 'Add region'}
          </Button>
        )
      },
    },
    {
      key: 'material',
      label: 'Material',
      formatter: (_: any, row: any) =>
        editingRowIndex !== null &&
        prescriptionDetails[editingRowIndex] === row ? (
          <Form.Select
            value={row.material}
            onChange={(e) =>
              onInputChange(editingRowIndex, 'material', e.target.value)
            }
            size="sm"
          >
            <option value="">Select</option>
            {renderOptions(materialOptions)}
          </Form.Select>
        ) : (
          <span>{row.material}</span>
        ),
    },
    {
      key: 'shadeNo',
      label: 'Shade No.',
      formatter: (_: any, row: any) =>
        editingRowIndex !== null &&
        prescriptionDetails[editingRowIndex] === row ? (
          <Form.Select
            value={row.shadeNo}
            onChange={(e) =>
              onInputChange(editingRowIndex, 'shadeNo', e.target.value)
            }
            size="sm"
          >
            <option value="">Select</option>
            {renderOptions(shadeNoOptions)}
          </Form.Select>
        ) : (
          <span>{row.shadeNo}</span>
        ),
    },
    {
      key: 'instructions',
      label: 'Instructions',
      formatter: (_: any, row: any) => (
        <Button
          variant="link"
          size="sm"
          className="p-0 text-decoration-underline"
          onClick={() => openNoteModal(row, prescriptionDetails.indexOf(row))}
        >
          {row.instructions ? 'View' : 'Add note'}
        </Button>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      formatter: (_: any, row: any) =>
        editingRowIndex !== null &&
        prescriptionDetails[editingRowIndex] === row ? (
          <ActionButtons isEditing onSave={onSave} />
        ) : (
          <ActionButtons
            onEdit={() => onEdit(prescriptionDetails.indexOf(row))}
            onDelete={() => onDelete(prescriptionDetails.indexOf(row))}
          />
        ),
    },
  ]

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-1">
        <h6 className="mb-0">Prescription details</h6>
        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={onAddItem}
          className="mb-1"
        >
          Add Item
        </Button>
      </div>

      <div className="prescription-details-table">
        <LabSharedTable
          columns={prescriptionColumns}
          data={prescriptionDetails}
          pageSize={5}
        />
      </div>

      <PrescriptionNote
        show={showNote}
        notes={
          noteRowIndex !== null
            ? [
                {
                  procedureName:
                    prescriptionDetails[noteRowIndex]?.procedureType || '',
                  instructions: currentNote,
                },
              ]
            : []
        }
        title={
          noteRowIndex !== null &&
          prescriptionDetails[noteRowIndex]?.instructions
            ? 'Edit note'
            : 'Add note'
        }
        onChange={(_, newText) => setCurrentNote(newText)}
        onSave={saveNote}
        onClose={() => {
          setShowNote(false)
          onToggleNoteModal(false)
        }}
      />

      <ApptToothModal
        isOpen={showRegionModal}
        closeModal={() => setShowRegionModal(false)}
        saveToothSelection={handleSaveRegion}
        skipToothSelection={() => {
          handleSaveRegion([{ tooth_num: 0, surf: [] }])
        }}
      />
    </>
  )
}

export default PrescriptionDetails
